import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibility } from '@/hooks/useAccessibility';
import logoBranca from '@/assets/logo_branca.png';
import logoAzul from '@/assets/logo_azul.webp';
import classroomPhoto from '@/assets/inclusive_classroom_neon.webp';

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'E-mail ou senha incorretos. Tente novamente.',
  'auth/invalid-login-credentials': 'E-mail ou senha incorretos. Tente novamente.',
  'auth/user-not-found': 'Não existe conta cadastrada com este e-mail.',
  'auth/wrong-password': 'Senha incorreta. Tente novamente.',
  'auth/invalid-email': 'E-mail inválido.',
  'auth/user-disabled': 'Esta conta foi desativada.',
  'auth/too-many-requests': 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
  'auth/network-request-failed': 'Falha de rede. Verifique sua conexão e tente novamente.',
  'auth/operation-not-allowed': 'Login por e-mail/senha não está habilitado no Firebase.',
  'auth/api-key-not-valid': 'Chave de API do Firebase inválida. Verifique as variáveis de ambiente.',
};

function getFirebaseErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  if (code && FIREBASE_ERROR_MESSAGES[code]) {
    return FIREBASE_ERROR_MESSAGES[code];
  }
  if (code) {
    return `Erro ao entrar (${code}). Verifique suas credenciais ou tente novamente.`;
  }
  return 'Erro ao entrar. Tente novamente.';
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, configured, signIn } = useAuth();
  const { speak, isDarkMode } = useAccessibility();
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await signIn(email.trim(), password);
      speak('Login realizado com sucesso');
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Falha no login com Firebase:', err);
      setError(getFirebaseErrorMessage(err));
      speak('Falha no login');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Grid vira bloco único no alto contraste (a foto some, o formulário ocupa tudo).
    // O fundo usa os tokens do tema (bg-background etc.) — acompanha claro/escuro/alto
    // contraste do site automaticamente, sem cor fixa.
    <div className="auth-shell relative min-h-screen bg-background lg:grid lg:grid-cols-[minmax(380px,440px)_1fr]">
      {/* Painel esquerdo — formulário */}
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-background px-6 sm:px-10 lg:px-16">
        {/* Logo — fica perto do topo, separado do formulário */}
        <div className="relative pt-8 sm:pt-10 lg:pt-12">
          <Link
            to="/"
            className="inline-flex items-center gap-3"
            onClick={() => speak('Início')}
          >
            <img
              src={isDarkMode ? logoBranca : logoAzul}
              alt="CONECTAR"
              className="h-8 w-auto object-contain sm:h-9"
            />
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight text-foreground">
                CONECTAR
              </span>
              <span className="block text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                Tecnologia Assistiva
              </span>
            </span>
          </Link>
        </div>

        {/* Formulário — centralizado no meio do espaço restante do painel */}
        <div className="relative flex flex-1 items-center py-10">
          <div className="mx-auto w-full max-w-sm animate-fade-in-up">
            <h1 className="mb-6 text-lg font-bold text-foreground">Entrar</h1>

            {!configured && (
              <p className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                Autenticação não configurada. Defina as variáveis Firebase em{' '}
                <code className="text-xs">.env.local</code> e reinicie o servidor.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!configured || submitting}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Senha</Label>
                  <Link
                    to="/redefinir-senha"
                    className="text-xs font-medium text-primary dark:text-white hover:underline"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!configured || submitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={!configured || submitting}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={!configured || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="mt-8 flex items-center justify-between text-sm">
              <Link to="/" className="text-muted-foreground hover:text-foreground hover:underline">
                Voltar ao início
              </Link>
              <Link
                to="/suporte"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                Precisa de ajuda?
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Painel direito — foto (decorativa: some no alto contraste, ver .photo-backdrop) */}
      <div className="photo-backdrop relative hidden lg:block">
        <img
          src={classroomPhoto}
          alt="Criança com fones de ouvido usa um app de comunicação alternativa em tablet, ao lado de uma educadora, numa sala com quadro de rotina visual"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

export default Login;
